import { ModelType } from '@/enums/model-type.enum';
import { runModelWithoutNode, type ModelRunObservation } from '@/api/runModel.api';
import { ModelRunError } from '@/api/errors';
import { useWorkflowStore } from '@/state/workflow.state';
import { assetUrl } from '@/utils/assetUrls';
import { validateFile } from '@/utils/nodeInputValidation';
import { track } from '@/utils/analytics';
import { CompositorAiError, CompositorAiErrorCode } from './compositorAiError';
import type { ModelRunCredits } from '@/api/modelPrediction';
import type { CompositorImageSource } from '@/components/Recipe/Views/CompositorView';
import type { CompositorAiTarget } from './CompositorAiTasks';
import type { Base64Image, FileKind, LayerId } from 'web';

export const NANO_BANANA_MODEL = {
  id: 'nano-banana-2',
  name: 'fal-ai/nano-banana-2/edit',
  version: 'fal-ai/nano-banana-2/edit',
  service: ModelType.FalImported,
  label: 'Nano Banana 2.1',
  isImported: false,
} as const;

export function getNanoBananaParams(width: number, height: number) {
  const longestEdge = Math.max(width, height);
  const resolution = longestEdge <= 1536 ? '1K' : longestEdge <= 3072 ? '2K' : '4K';
  return { num_images: 1, model: 'Nano Banana 2.1', resolution };
}

export type ImageEditResult = Extract<FileKind, { type: 'image' }>;
export type ImageEditRunner = (
  target: CompositorAiTarget & { runId: string; prompt: string },
  observation?: ModelRunObservation,
) => Promise<ImageEditResult>;

export interface ImageEditSource {
  url: string;
  width: number;
  height: number;
}

export function createNanoBananaRunner(
  action: 'inpaint' | 'outpaint',
  systemPrompt: string,
  getSource: (layerId: LayerId) => CompositorImageSource | undefined,
  prepareGuide: () => Base64Image | undefined,
  onCredits?: (credits: ModelRunCredits) => void,
  getParams = getNanoBananaParams,
  prepareSource?: (source: ImageEditSource, signal?: AbortSignal) => Promise<ImageEditSource>,
): ImageEditRunner {
  const model = NANO_BANANA_MODEL;
  return async ({ nodeId, layerId, runId, prompt }, observation) => {
    const { recipe: workflow, resolvedAssets } = useWorkflowStore.getState();
    if (!workflow.id || !Number.isFinite(workflow.version))
      throw new CompositorAiError(CompositorAiErrorCode.WorkflowRequired);
    const source = getSource(layerId);
    if (
      !source ||
      !(source.file.width > 0) ||
      !(source.file.height > 0) ||
      !Number.isFinite(source.file.width + source.file.height)
    ) {
      throw new CompositorAiError(CompositorAiErrorCode.SourceUnavailable);
    }
    const imageUrl = assetUrl(source.file, resolvedAssets);
    if (!imageUrl || (!imageUrl.startsWith('https://') && !imageUrl.startsWith('data:image/'))) {
      throw new CompositorAiError(CompositorAiErrorCode.SourceNotUploaded);
    }
    const guide = prepareGuide();
    if (
      !guide ||
      !guide.data.startsWith('data:image/') ||
      !(guide.width > 0) ||
      !(guide.height > 0) ||
      !Number.isFinite(guide.width + guide.height)
    ) {
      throw new CompositorAiError(CompositorAiErrorCode.SourceUnavailable);
    }
    const startedAt = performance.now();
    let returnedCount: number | undefined;
    let outcome = 'failed';
    try {
      observation?.signal?.throwIfAborted();
      const original = { url: imageUrl, width: source.file.width, height: source.file.height };
      let input = original;
      if (prepareSource) {
        const preparation = new AbortController();
        observation?.onCancelReady?.(() => {
          preparation.abort(new ModelRunError('canceled'));
          return Promise.resolve();
        });
        const signal = observation?.signal
          ? AbortSignal.any([preparation.signal, observation.signal])
          : preparation.signal;
        input = await prepareSource(original, signal);
        signal.throwIfAborted();
      }
      observation?.signal?.throwIfAborted();
      const results = await runModelWithoutNode(
        {
          model: {
            name: model.name,
            version: model.version,
            type: model.service,
            isImported: model.isImported,
          },
          input: {
            ...getParams(input.width, input.height),
            images: [input.url, guide.data],
            prompt: `${systemPrompt}\n\nUser instructions: ${prompt}`,
          },
          recipeId: workflow.id,
          recipeVersion: workflow.version,
        },
        onCredits,
        observation,
      );
      returnedCount = Array.isArray(results) ? results.length : undefined;
      if (useWorkflowStore.getState().recipe.id !== workflow.id)
        throw new CompositorAiError(CompositorAiErrorCode.SourceChanged);
      if (!Array.isArray(results) || results.length === 0)
        throw new CompositorAiError(CompositorAiErrorCode.EmptyResult);
      const image = validateFile(results[0]);
      if (
        image?.type !== 'image' ||
        !(image.width > 0) ||
        !(image.height > 0) ||
        !Number.isFinite(image.width + image.height)
      ) {
        throw new CompositorAiError(CompositorAiErrorCode.InvalidResult);
      }
      outcome = 'success';
      return image;
    } catch (error) {
      if (observation?.signal?.aborted || (error instanceof ModelRunError && error.kind === 'canceled'))
        outcome = 'canceled';
      throw error;
    } finally {
      track('compositor_ai_model_run', {
        execution_origin: 'compositor_action',
        action_id: action,
        run_id: runId,
        workflow_id: workflow.id,
        node_id: nodeId,
        layer_id: layerId,
        model_id: model.name,
        source_kind: source.kind,
        image_width: source.file.width,
        image_height: source.file.height,
        layer_count: returnedCount,
        outcome,
        duration_ms: Math.round(performance.now() - startedAt),
      });
    }
  };
}
